import type { CaptionStyle } from "@teksta/shared-types";
import type { CaptionBox } from "./captions";

/**
 * Pure math for direct manipulation of the on-stage caption box, shared by the
 * subtitle editor's stage and the composition editor's stage so the two feel
 * identical. The interaction model is align + vertical offset, not free x/y:
 * vertical drag sets position/offset (snapping to top/center/bottom zones),
 * horizontal drag snaps alignment to left/center/right, and the corner handle
 * scales the font by the pointer's distance from the box's anchor.
 */

export const CAPTION_FONT_MIN = 0.03;
export const CAPTION_FONT_MAX = 0.12;
export const CAPTION_OFFSET_MAX = 0.3;

const clamp = (value: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, value));

/** The displayed media area, in the same coordinate space as `box` and `point`. */
export interface StageContentRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Style patch for a move drag: `point` is the current pointer position and
 * `grab` its offset from the box's top-left at grab time (all in the stage
 * container's coordinate space, with `box` the caption's measured display
 * bounds there).
 */
export function captionMovePatch(
  style: Pick<CaptionStyle, "position" | "offsetRel" | "maxWidthRel">,
  contentRect: StageContentRect,
  box: CaptionBox,
  point: { x: number; y: number },
  grab: { dx: number; dy: number }
): Pick<CaptionStyle, "position" | "offsetRel" | "align"> {
  const boxHFrac = box.height / contentRect.height;
  const localTop = point.y - grab.dy;
  const topF = clamp(
    (localTop - contentRect.top) / contentRect.height,
    0,
    Math.max(0, 1 - boxHFrac)
  );
  const centerF = topF + boxHFrac / 2;

  let position: CaptionStyle["position"];
  let offsetRel = style.offsetRel;
  if (centerF < 1 / 3) {
    position = "top";
    offsetRel = clamp(topF, 0, CAPTION_OFFSET_MAX);
  } else if (centerF > 2 / 3) {
    position = "bottom";
    offsetRel = clamp(1 - boxHFrac - topF, 0, CAPTION_OFFSET_MAX);
  } else {
    position = "center";
  }

  // Horizontal: snap to the nearest of the three alignment slots by where the
  // dragged box's left edge lands. (A box-center test is unreachable for wide
  // captions, which is why sideways drag did nothing before.)
  const cw = contentRect.width;
  const maxWidth = style.maxWidthRel * cw;
  const marginX = (cw - maxWidth) / 2;
  const draggedLeft = point.x - grab.dx - contentRect.left;
  const slots: [CaptionStyle["align"], number][] = [
    ["left", marginX],
    ["center", (cw - box.width) / 2],
    ["right", cw - marginX - box.width],
  ];
  const align = slots.reduce((best, slot) =>
    Math.abs(slot[1] - draggedLeft) < Math.abs(best[1] - draggedLeft) ? slot : best
  )[0];

  return { position, offsetRel, align };
}

/**
 * Placement patch for a free-move drag of a label: maps the pointer to the
 * dragged box's new anchor point in frame fractions — `align` picks the
 * horizontal anchor (left edge / center / right edge, mirroring the render
 * semantics) and the vertical anchor is always the box center. Coordinates
 * are the stage container's, with `contentRect` the displayed media area
 * (same contract as `captionMovePatch`). The box position is clamped so it
 * stays fully inside the content rect (flush allowed) before the anchor is
 * derived, so drag preview and render agree.
 */
export function labelMovePatch(
  align: CaptionStyle["align"],
  contentRect: StageContentRect,
  box: CaptionBox,
  point: { x: number; y: number },
  grab: { dx: number; dy: number }
): { xRel: number; yRel: number } {
  const wFrac = box.width / contentRect.width;
  const hFrac = box.height / contentRect.height;
  // Clamp the box's top-left into the rect (mirrors the render clamp's
  // shift-never-shrink, pinning to the start edge when oversized).
  const clampStart = (startFrac: number, sizeFrac: number) =>
    sizeFrac >= 1 ? 0 : clamp(startFrac, 0, 1 - sizeFrac);
  const leftFrac = clampStart((point.x - grab.dx - contentRect.left) / contentRect.width, wFrac);
  const topFrac = clampStart((point.y - grab.dy - contentRect.top) / contentRect.height, hFrac);

  return {
    xRel: align === "left" ? leftFrac : align === "right" ? leftFrac + wFrac : leftFrac + wFrac / 2,
    yRel: topFrac + hFrac / 2,
  };
}

/**
 * Font size for a resize drag: the font scales with the pointer's distance from
 * the fixed anchor relative to its distance at grab time, clamped to the
 * editable range.
 */
export function captionResizeFontSizeRel(
  startFontSizeRel: number,
  startDist: number,
  dist: number
): number {
  return clamp(startFontSizeRel * (dist / startDist), CAPTION_FONT_MIN, CAPTION_FONT_MAX);
}
